/**
 * Regalos automáticos por colección (Carter's Iberia).
 * - Reglas en snippets/cart-collection-gifts.liquid (script[data-cart-collection-gifts-config]).
 * - Cuando el carrito cumple la condición, añade el regalo (qty 1) con una propiedad de línea;
 *   si deja de cumplirla, lo quita. El precio 0 € lo pone el descuento automático de Shopify
 *   («Compra X lleva Y»), este script solo añade/quita la línea.
 * - Si el cliente quita el regalo a mano, no se vuelve a añadir en esa sesión
 *   (hasta que deje de cumplir la condición y la vuelva a cumplir).
 * - No acumulable con cupones: Shopify solo aplica un descuento de producto por artículo, así que si
 *   el cliente mete un cupón y Shopify elige el cupón, el regalo dejaría de ser gratis. En ese caso
 *   se quita el regalo automático y se avisa en el carrito, con un botón para quitar el cupón y
 *   quedarse con el regalo (decisión de Amanda, 23/10/2026).
 * - Modo prueba: si config.enabled es false, solo funciona con ?probar_regalos=1 en la URL
 *   (queda guardado en la sesión; ?probar_regalos=0 lo apaga).
 */
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

const SOURCE = 'cart-collection-gifts';
const SS_PREFIX = 'ccg:';
const TEST_KEY = SS_PREFIX + 'test';

let busy = false;
let queued = false;
let timer = null;
let progress = [];
let cartTotal = 0;
let cfg = null;

const STYLE_ID = 'ccg-progress-style';
function ensureStyle() {
  if (document.getElementById(STYLE_ID)) return;
  const st = document.createElement('style');
  st.id = STYLE_ID;
  st.textContent =
    '.ccg-progress{display:grid;gap:8px;margin:0 0 12px}' +
    '.ccg-p{border-radius:12px;padding:10px 12px;font-size:13px;line-height:1.35}' +
    '.ccg-p b{font-weight:700}' +
    '.ccg-p__bar{height:6px;border-radius:99px;background:rgba(255,255,255,.25);margin-top:7px;overflow:hidden}' +
    '.ccg-p__bar i{display:block;height:100%;border-radius:99px;background:var(--ccg-acc);transition:width .4s}' +
    '.ccg-p--okb{background:#1C3557;color:#F4EEE3;--ccg-acc:#E9A23B}' +
    '.ccg-p--hw{background:#5E17C9;color:#fff;--ccg-acc:#FF8A1F}' +
    '.ccg-p__ship{margin-top:6px;font-size:12px;opacity:.85}' +
    '.ccg-progress--top{margin:8px 0 0}' +
    '.ccg-progress--top .ccg-p--okb{background:#EEF3F8;color:#1C3557;padding:8px 12px;font-size:12.5px}' +
    '.ccg-progress--top .ccg-p--okb .ccg-p__bar{background:rgba(28,53,87,.12);height:5px}' +
    '.ccg-p__note{margin-top:6px;font-size:11px;opacity:.75}' +
    '.ccg-p--conflict{background:#FFF4E5;color:#5A3A00;border:1px solid #F2C78A}' +
    '.ccg-p__btn{display:inline-block;margin-top:8px;padding:7px 12px;border-radius:999px;border:0;cursor:pointer;' +
    'background:#1C3557;color:#fff;font:600 12px/1.2 inherit}';
  document.head.appendChild(st);
}

function money(cents) {
  const v = (cents / 100).toFixed(2).replace('.', ',');
  return v.replace(',00', '') + ' €';
}

function renderProgress() {
  document.querySelectorAll('.ccg-progress').forEach((e) => e.remove());
  document.querySelectorAll('.cart-drawer__progress-block [data-type="free_shipping"]').forEach((e) => { e.style.display = ''; });
  const rows = progress.filter((p) => p.value > 0 && p.rule.progress_need && (!p.qualifies || p.inCart || p.conflict));
  if (!rows.length) return;
  ensureStyle();
  const shipMin = Math.round(Number(cfg?.ship_min || 0) * 100);
  const shipGap = shipMin ? Math.max(0, shipMin - cartTotal) : null;
  // El envío gratis lo muestra la barra propia del carrito; aquí solo el regalo.
  const hasFs = !!document.querySelector('.cart-drawer__progress-block [data-type="free_shipping"]');
  let shipUsed = shipGap === null || hasFs;
  const html = rows.map((p, idx) => {
    const r = p.rule;
    const pct = Math.min(100, Math.round((p.value / p.min) * 100));
    let msg;
    let extra = '';
    if (p.conflict && r.conflict_text) {
      const code = p.conflict.join(', ');
      return '<div class="ccg-p ccg-p--conflict">' + r.conflict_text.replace('{code}', code) +
        '<br><button type="button" class="ccg-p__btn" data-ccg-use-gift="' + r.id + '">' + (r.conflict_button || '') + '</button></div>';
    }
    if (p.qualifies) {
      msg = r.progress_done;
      if (!shipUsed) {
        msg += shipGap === 0 ? ' · ' + cfg.ship_ok : '';
        if (shipGap > 0) extra = cfg.ship_left.replace('{x}', money(shipGap));
        shipUsed = true;
      }
    } else {
      const left = p.min - p.value;
      const x = r.type === 'amount' ? money(left) : String(left);
      msg = (left === 1 && r.progress_need_one ? r.progress_need_one : r.progress_need).replace('{x}', x);
      if (!shipUsed) {
        if (shipGap === 0) msg += ' · ' + cfg.ship_ok;
        else if (r.type === 'amount' && Math.abs(shipGap - left) <= 300) msg = msg.replace(money(left), money(Math.max(left, shipGap))) + cfg.ship_and;
        else extra = cfg.ship_left.replace('{x}', money(shipGap));
        shipUsed = true;
      }
    }
    return '<div class="ccg-p ccg-p--' + (r.style || 'okb') + '">' + msg +
      '<div class="ccg-p__bar"><i style="width:' + pct + '%"></i></div>' +
      (extra ? '<div class="ccg-p__ship">' + extra + '</div>' : '') +
      (r.no_stack_note ? '<div class="ccg-p__note">' + r.no_stack_note + '</div>' : '') + '</div>';
  }).join('');
  document.querySelectorAll('cart-items-component').forEach((comp) => {
    const box = document.createElement('div');
    box.className = 'ccg-progress';
    box.setAttribute('role', 'status');
    box.innerHTML = html;
    const fs = comp.querySelector('.cart-drawer__progress-block [data-type="free_shipping"]');
    if (fs) {
      fs.after(box);
      box.classList.add('ccg-progress--top');
      return;
    }
    const w = comp.querySelector('.cart-items__wrapper');
    if (w) w.prepend(box);
  });
}

const ss = {
  get(k) { try { return sessionStorage.getItem(SS_PREFIX + k); } catch { return null; } },
  set(k, v) { try { sessionStorage.setItem(SS_PREFIX + k, v); } catch {} },
  del(k) { try { sessionStorage.removeItem(SS_PREFIX + k); } catch {} },
};

function getConfig() {
  const el = document.querySelector('script[data-cart-collection-gifts-config]');
  if (!el) return null;
  try {
    const data = JSON.parse(el.textContent || '{}');
    if (!Array.isArray(data.rules)) return null;
    return data;
  } catch {
    return null;
  }
}

function isActive(config) {
  try {
    const p = new URLSearchParams(location.search).get('probar_regalos');
    if (p === '1') sessionStorage.setItem(TEST_KEY, '1');
    if (p === '0') sessionStorage.removeItem(TEST_KEY);
  } catch {}
  if (config.enabled) return true;
  try { return sessionStorage.getItem(TEST_KEY) === '1'; } catch { return false; }
}

function routes() {
  const r = globalThis.Theme?.routes || {};
  const cartBase = (r.cart_url || '/cart').replace(/\.js$/, '');
  const add = r.cart_add_url || '/cart/add';
  const change = r.cart_change_url || '/cart/change';
  return {
    cart: `${cartBase}.js`,
    add: add.endsWith('.js') ? add : `${add}.js`,
    change: change.endsWith('.js') ? change : `${change}.js`,
  };
}

/** IDs de producto de una colección (products.json público, cache 30 min en sesión). */
async function collectionProductIds(url) {
  const key = 'col:' + url;
  const cached = ss.get(key);
  if (cached) {
    try {
      const { t, ids } = JSON.parse(cached);
      if (Date.now() - t < 30 * 60 * 1000) return new Set(ids);
    } catch {}
  }
  const ids = [];
  for (let page = 1; page <= 4; page++) {
    const res = await fetch(`${url.replace(/\/$/, '')}/products.json?limit=250&page=${page}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) break;
    const data = await res.json();
    const products = data.products || [];
    products.forEach((p) => ids.push(Number(p.id)));
    if (products.length < 250) break;
  }
  ss.set(key, JSON.stringify({ t: Date.now(), ids }));
  return new Set(ids);
}

function ruleIsLive(rule) {
  const now = Date.now();
  if (rule.starts_at && now < Date.parse(rule.starts_at)) return false;
  if (rule.ends_at && now > Date.parse(rule.ends_at)) return false;
  return true;
}

/** Valor que cuenta para la regla: unidades o importe (céntimos) de la colección, sin contar regalos. */
function qualifyingValue(cart, rule, productIds, giftVariantIds) {
  let qty = 0;
  let amount = 0;
  for (const item of cart.items || []) {
    if (giftVariantIds.has(Number(item.variant_id))) continue;
    if (!productIds.has(Number(item.product_id))) continue;
    qty += item.quantity;
    amount += item.final_line_price ?? item.line_price ?? 0;
  }
  return rule.type === 'amount' ? amount : qty;
}

function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return [...ids];
}

async function refresh(sections) {
  for (const sid of sectionIds()) {
    let html = sections?.[sid];
    if (!html) html = await sectionRenderer.getSectionHTML(sid, false);
    if (!html) continue;
    document.dispatchEvent(new CartUpdateEvent({}, sid, { source: SOURCE, sections: { [sid]: html } }));
  }
}

async function post(url, body) {
  const r = await fetch(url, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) console.warn('[regalos]', data.description || data.message || r.status);
  return { ok: r.ok, data };
}

function showNotice(text) {
  if (!text) return;
  let el = document.getElementById('ccg-notice');
  if (!el) {
    el = document.createElement('div');
    el.id = 'ccg-notice';
    el.setAttribute('role', 'status');
    el.style.cssText =
      'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:2147483000;' +
      'background:#0B3B4A;color:#fff;padding:12px 18px;border-radius:999px;font:600 14px/1.3 inherit;' +
      'box-shadow:0 6px 20px rgba(0,0,0,.18);max-width:90vw;text-align:center;transition:opacity .3s';
    document.body.appendChild(el);
  }
  el.textContent = text;
  el.style.opacity = '1';
  clearTimeout(el._t);
  el._t = setTimeout(() => { el.style.opacity = '0'; }, 4000);
}

async function sync() {
  const config = getConfig();
  if (!config || !isActive(config)) return;
  if (busy) { queued = true; return; }
  busy = true;

  try {
    const { cart: cartUrl, add, change } = routes();
    const res = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
    if (!res.ok) return;
    const cart = await res.json();
    const sections = sectionIds().join(',');
    const giftVariantIds = new Set(config.rules.map((r) => Number(r.gift_variant_id)));

    let changed = false;
    let lastSections;
    const notices = [];
    progress = [];
    cartTotal = Number(cart.total_price) || 0;
    cfg = config;

    for (const rule of config.rules) {
      const giftId = Number(rule.gift_variant_id);
      if (!giftId || !rule.collection_url) continue;
      const lines = (cart.items || []).filter((i) => Number(i.variant_id) === giftId);
      const autoLines = lines.filter((i) => i.properties && i.properties[config.property_name] != null);
      const live = ruleIsLive(rule);

      let qualifies = false;
      let pr = null;
      if (live) {
        const productIds = await collectionProductIds(rule.collection_url);
        const value = qualifyingValue(cart, rule, productIds, giftVariantIds);
        const min = rule.type === 'amount' ? Math.round(Number(rule.min) * 100) : Number(rule.min);
        qualifies = value >= min;
        pr = { rule, value, min, qualifies, inCart: false };
        progress.push(pr);
      }

      const addedKey = 'added:' + giftId;
      const declinedKey = 'declined:' + giftId;

      if (!qualifies) {
        ss.del(declinedKey);
        ss.del(addedKey);
        for (const line of autoLines) {
          const r = await post(change, { id: line.key, quantity: 0, sections, sections_url: location.pathname });
          if (r.ok) { changed = true; lastSections = r.data.sections; }
        }
        continue;
      }

      const codes = (cart.discount_codes || []).filter((c) => c.applicable).map((c) => c.code);
      const conflictKey = 'conflict:' + giftId;

      // Cumple la condición, pero un cupón le gana al regalo: el regalo se estaría cobrando.
      if (lines.length && codes.length && lines.some((l) => (l.final_line_price ?? l.line_price) > 0)) {
        for (const line of autoLines) {
          const r = await post(change, { id: line.key, quantity: 0, sections, sections_url: location.pathname });
          if (r.ok) { changed = true; lastSections = r.data.sections; }
        }
        ss.set(conflictKey, codes.join(','));
        ss.del(addedKey); // lo quitamos nosotros: que no cuente como «el cliente lo quitó»
        if (pr) pr.conflict = codes;
        if (rule.conflict_notice) notices.push(rule.conflict_notice);
        continue;
      }
      if (!lines.length && codes.length && ss.get(conflictKey) === codes.join(',')) {
        if (pr) pr.conflict = codes;
        continue;
      }
      if (!codes.length) ss.del(conflictKey);

      // Cumple la condición
      if (lines.length) {
        if (pr) pr.inCart = true;
        ss.set(addedKey, '1');
        // Si el regalo automático tiene más de 1 unidad, se deja en 1
        for (const line of autoLines) {
          if (line.quantity > 1) {
            const r = await post(change, { id: line.key, quantity: 1, sections, sections_url: location.pathname });
            if (r.ok) { changed = true; lastSections = r.data.sections; }
          }
        }
        continue;
      }

      // No está en el carrito: ¿lo quitó el cliente?
      if (ss.get(addedKey) === '1') ss.set(declinedKey, '1');
      if (ss.get(declinedKey) === '1') continue;

      const r = await post(add, {
        items: [{ id: giftId, quantity: 1, properties: { [config.property_name]: rule.label } }],
        sections,
        sections_url: location.pathname,
      });
      if (r.ok) {
        changed = true;
        lastSections = r.data.sections;
        ss.set(addedKey, '1');
        if (pr) pr.inCart = true;
        // Con cupón puesto, comprobar que el regalo ha quedado gratis; si no, quitarlo y avisar.
        let freeOk = true;
        if (codes.length) {
          const c2 = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((x) => x.json()).catch(() => null);
          const gl = (c2?.items || []).filter((i) => Number(i.variant_id) === giftId && i.properties && i.properties[config.property_name] != null);
          if (gl.length && gl.some((l) => (l.final_line_price ?? l.line_price) > 0)) {
            freeOk = false;
            for (const line of gl) {
              const r2 = await post(change, { id: line.key, quantity: 0, sections, sections_url: location.pathname });
              if (r2.ok) lastSections = r2.data.sections;
            }
            ss.set(conflictKey, codes.join(','));
            ss.del(addedKey);
            if (pr) { pr.inCart = false; pr.conflict = codes; }
            if (rule.conflict_notice) notices.push(rule.conflict_notice);
          }
        }
        if (freeOk) notices.push(rule.notice);
      }
    }

    if (changed) await refresh(lastSections);
    if (notices.length) showNotice(notices.join(' · '));
    renderProgress();
  } catch (e) {
    console.warn('[regalos] error', e);
  } finally {
    busy = false;
    if (queued) { queued = false; sync(); }
  }
}

function onCartUpdate(event) {
  setTimeout(renderProgress, 60);
  if (event.detail?.data?.source === SOURCE) return;
  clearTimeout(timer);
  timer = setTimeout(sync, 250);
}

document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);
// Al poner o quitar un cupón en el carrito, volver a comprobar los regalos.
document.addEventListener('discount:update', () => { clearTimeout(timer); timer = setTimeout(sync, 250); });

// Botón «Prefiero el regalo»: quita el/los cupones y vuelve a añadir el regalo.
document.addEventListener('click', async (e) => {
  const btn = e.target instanceof Element ? e.target.closest('[data-ccg-use-gift]') : null;
  if (!btn) return;
  e.preventDefault();
  btn.setAttribute('disabled', '');
  const config = getConfig();
  const rule = config?.rules.find((x) => x.id === btn.getAttribute('data-ccg-use-gift'));
  const giftId = Number(rule?.gift_variant_id);
  const r = globalThis.Theme?.routes || {};
  const upd = (r.cart_update_url || '/cart/update').replace(/\.js$/, '') + '.js';
  await post(upd, { discount: '', sections: sectionIds().join(','), sections_url: location.pathname });
  if (giftId) { ss.del('conflict:' + giftId); ss.del('declined:' + giftId); ss.del('added:' + giftId); }
  await refresh();
  sync();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', sync);
} else {
  sync();
}
